import { useState, useCallback, useEffect } from 'react'
import { login as apiLogin } from '@/api/auth'
import { decodeToken, isTokenExpired, type JwtPayload } from '@/lib/token'
import { setUnauthorizedHandler } from '@/api/client'

const TOKEN_KEY = 'isapi_token'

export interface AuthState {
  token: string | null
  user: JwtPayload | null
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

export function useAuth(): AuthState {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored && !isTokenExpired(stored)) return stored
    if (stored) localStorage.removeItem(TOKEN_KEY)
    return null
  })

  const user = token ? decodeToken(token) : null

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setToken(null)
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const result = await apiLogin(email, password)
    localStorage.setItem(TOKEN_KEY, result.token)
    setToken(result.token)
  }, [])

  useEffect(() => {
    setUnauthorizedHandler(logout)
  }, [logout])

  return {
    token,
    user,
    isAuthenticated: token !== null,
    login,
    logout,
  }
}
